import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useRouter } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { listImagesApiImagesGetOptions } from "../generated/api/@tanstack/react-query.gen";
import type { HydratedImage } from "../generated/api/types.gen";
import type { AsyncError } from "../lib/errors";
import { generateImages, shouldAutoTriggerGeneration } from "../lib/generation";

interface UseImageStreamingProps {
  requestId: string;
  prompt?: string;
  imageFiles?: (File | null)[] | null;
  referenceAssetIds?: (string | null)[] | null;
  sessionId?: string;
  projectId?: string;
}

export function useImageStreaming({
  requestId,
  prompt,
  imageFiles,
  referenceAssetIds,
  sessionId,
  projectId,
}: UseImageStreamingProps) {
  const navigate = useNavigate();
  const router = useRouter();
  const queryClient = useQueryClient();
  const [streamError, setStreamError] = useState<AsyncError>(null);
  const triggeredRef = useRef<string | null>(null);

  const {
    data: imagesData,
    isFetched,
    isLoading,
  } = useQuery({
    ...listImagesApiImagesGetOptions({
      query: { request_id: requestId },
    }),
    enabled: Boolean(requestId),
    select: (data) => (data.images ?? []) as HydratedImage[],
  });

  const images = imagesData;

  const { isPending: isGenerating, mutate: triggerGeneration } = useMutation({
    mutationFn: () =>
      generateImages({
        prompt,
        requestId,
        sessionId,
        projectId,
        imageFiles,
        referenceAssetIds,
        queryClient,
        router,
        setStreamError,
      }),
  });

  useEffect(() => {
    if (
      shouldAutoTriggerGeneration({
        hasImages: Boolean(images?.length),
        isFetched,
        lastTriggered: triggeredRef.current,
        requestId,
      })
    ) {
      triggeredRef.current = requestId;
      triggerGeneration();
    }
  }, [images?.length, isFetched, requestId, triggerGeneration]);

  useEffect(() => {
    if (!requestId) {
      navigate({ to: "/" });
      return;
    }
  }, [requestId, navigate]);

  return {
    images,
    isInitialLoading: isLoading || isGenerating,
    error: streamError,
  };
}
